const NEXUS_API = 'https://api.tommyguns.com/checkins/v1';
const NEXUS_HUB = 'https://nexus.tommyguns.com/checkins/events';
const SIGNALR_SRC = 'https://cdn.jsdelivr.net/npm/@microsoft/signalr@8.0.29/dist/browser/signalr.min.js';
const NEXUS_TOKEN_KEY = 'tg-nexus-token';
const WAIT_STALE_MS = 60000;
// Closed/Full depend on the clock, so status is re-evaluated on a tick like the Checkins app
const STATUS_TICK_MS = 30000;

// The theme's "Disable wait times" setting puts its replacement text on <html>
const WAIT_TIMES_DISABLED_TEXT = document.documentElement.dataset.waitTimesDisabledText;
const WAIT_TIMES_DISABLED = WAIT_TIMES_DISABLED_TEXT !== undefined;

// Joining the franchise group streams hub events for every shop in the franchise;
// without it pages only make one-shot REST fetches
const LIVE_UPDATES = !WAIT_TIMES_DISABLED;

const NexusWaits = {
  token: null,
  tokenRequest: null,
  shops: new Map(),
  views: new Map(),
  hub: null,
  hubStart: null,
  hubToggle: Promise.resolve(),
  groups: new Set(),

  storedToken() {
    try {
      return JSON.parse(sessionStorage.getItem(NEXUS_TOKEN_KEY));
    } catch (error) {
      return null;
    }
  },

  async getToken(refresh = false) {
    const cached = refresh ? null : this.token || this.storedToken();
    if (cached?.value && cached.exp * 1000 - Date.now() > 60000) {
      this.token = cached;
      return cached.value;
    }

    this.tokenRequest ||= fetch(`${NEXUS_API}/auth/token/public`, { method: 'POST', credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error(`Token request failed: ${response.status}`);
        return response.json();
      })
      .then(({ token }) => {
        const claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        this.token = { value: token, exp: claims.exp };
        try {
          sessionStorage.setItem(NEXUS_TOKEN_KEY, JSON.stringify(this.token));
        } catch (error) {}
        return token;
      })
      .finally(() => {
        this.tokenRequest = null;
      });

    return this.tokenRequest;
  },

  async request(path, retry = true) {
    const token = await this.getToken(!retry);
    const response = await fetch(`${NEXUS_API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (response.status === 401 && retry) return this.request(path, false);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  },

  fetchShop(shopId) {
    const entry = this.shops.get(shopId);
    if (entry?.pending) return entry.pending;
    if (entry?.data && (this.hub?.state === 'Connected' || Date.now() - entry.fetchedAt < WAIT_STALE_MS)) {
      return Promise.resolve(entry.data);
    }

    const pending = this.request(`/shops/${encodeURIComponent(shopId)}`)
      .then((data) => {
        this.shops.set(shopId, { data, fetchedAt: Date.now() });
        if (LIVE_UPDATES) this.joinFranchise(data.franchiseId);
        return data;
      })
      .catch((error) => {
        this.shops.delete(shopId);
        throw error;
      });

    this.shops.set(shopId, { ...entry, pending });
    return pending;
  },

  joinFranchise(franchiseId) {
    if (!franchiseId || this.groups.has(franchiseId)) return;
    this.groups.add(franchiseId);

    this.hubStart ||= this.startHub().catch((error) => {
      console.warn('[StoreStatus] Live wait times unavailable', error);
      return null;
    });

    this.hubStart.then((hub) => hub?.invoke('JoinFranchiseGroup', franchiseId)).catch(() => {});
  },

  async startHub() {
    if (!window.signalR) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SIGNALR_SRC;
        script.onload = resolve;
        script.onerror = () => reject(new Error('SignalR failed to load'));
        document.head.appendChild(script);
      });
    }

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(NEXUS_HUB, { accessTokenFactory: () => this.getToken() })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    // Same events the Checkins app consumes: both wait events carry minutes + isOnline,
    // an hours change means the cached shop record is stale
    hub.on('ShopWaitDetailedUpdated', (payload) => this.applyUpdate(payload));
    hub.on('ShopWaitOverallUpdated', (payload) => this.applyUpdate(payload));
    hub.on('ShopHoursUpdated', ({ shopId }) => this.refreshShop(shopId));
    // Group membership belongs to the connection, so a reconnect has to rejoin
    hub.onreconnected(() => {
      this.groups.forEach((franchiseId) => hub.invoke('JoinFranchiseGroup', franchiseId));
      this.refresh();
    });

    await hub.start();
    this.hub = hub;
    return hub;
  },

  // The franchise stream is several events per second, so the hub only runs while the page is visible
  // and at least one wait time is being shown
  syncHub() {
    this.hubToggle = Promise.all([this.hubToggle, this.hubStart])
      .then(async () => {
        if (!this.hub) return;

        const wanted = !document.hidden && [...this.views.values()].some((elements) => elements.size);
        if (!wanted) {
          await this.hub.stop();
          return;
        }
        if (this.hub.state !== 'Disconnected') return;

        await this.hub.start();
        await Promise.all([...this.groups].map((franchiseId) => this.hub.invoke('JoinFranchiseGroup', franchiseId)));
        await this.refresh();
      })
      .catch(() => {});
  },

  async refreshShop(shopId) {
    if (!this.shops.has(shopId)) return;
    this.shops.delete(shopId);
    try {
      await this.fetchShop(shopId);
      this.render(shopId);
    } catch (error) {}
  },

  refresh() {
    return Promise.all([...this.shops.keys()].map((shopId) => this.refreshShop(shopId)));
  },

  applyUpdate({ shopId, minutes, isOnline }) {
    const entry = this.shops.get(shopId);
    if (!entry?.data) return;
    if (entry.data.waitMinutes === minutes && entry.data.isOnline === isOnline) return;

    entry.data = { ...entry.data, waitMinutes: minutes, isOnline };
    entry.fetchedAt = Date.now();
    this.render(shopId);
  },

  render(shopId, { onlyIfChanged = false } = {}) {
    const entry = this.shops.get(shopId);
    if (!entry?.data) return;

    const status = StoreStatus.toShopStatus(entry.data);
    const key = JSON.stringify(status);
    if (onlyIfChanged && entry.renderedKey === key) return;
    entry.renderedKey = key;

    const instance = StoreStatus.create();
    const views = this.views.get(shopId);
    views?.forEach((element) => {
      if (element.isConnected) {
        instance.updateWaitTime(element, status);
      } else {
        views.delete(element);
      }
    });

    document.dispatchEvent(new CustomEvent('shop-wait:updated', { detail: { shopId, data: status } }));
  },

  tick() {
    if (document.hidden) return;
    this.shops.forEach((entry, shopId) => this.render(shopId, { onlyIfChanged: true }));
  },

  watch(shopId, element) {
    if (!this.views.has(shopId)) this.views.set(shopId, new Set());
    this.views.get(shopId).add(element);
    this.syncHub();
  },

  unwatch(element) {
    this.views.forEach((elements) => elements.delete(element));
    this.syncHub();
  }
};

const waitTimeSwaps = new WeakMap();

class StoreStatus {
  async fetchShopStatus(shopId) {
    try {
      return StoreStatus.toShopStatus(await NexusWaits.fetchShop(shopId));
    } catch (error) {
      return {
        shopStatus: 'Error',
        waitTime: 0,
        upcomingOpenTime: ''
      };
    }
  }

  async fetchShopStatuses(shopIds) {
    // Fetch all store statuses in parallel
    const results = await Promise.all(shopIds.map(shopId => this.fetchShopStatus(shopId)));

    // Convert array of results to object with shopId as key
    return shopIds.reduce((acc, shopId, index) => {
      acc[shopId] = results[index];
      return acc;
    }, {});
  }

  // Status rules mirror the Checkins app (ShopStatusHelper / ShopCheckinStatusEvaluator):
  // Offline, then check-ins disabled, then Closed and Full computed from the shop-local clock
  static toShopStatus(shop) {
    const waitTime = Math.max(0, shop.waitMinutes || 0);

    if (shop.isOnline === false) {
      return { shopStatus: 'Offline', waitTime: 0, upcomingOpenTime: '' };
    }

    if (shop.checkinsEnabled === false) {
      return {
        shopStatus: 'Disabled',
        waitTime: 0,
        upcomingOpenTime: '',
        message: (shop.checkinsDisabledMessage || '').trim() || 'Check-in is unavailable at this shop.'
      };
    }

    const local = StoreStatus.shopLocalNow(shop.timeZoneId);
    const hours = StoreStatus.todayHours(shop, local.date);
    const closed = StoreStatus.isClosedNow(shop, local, hours);
    const full = !closed && waitTime > 0 && hours.close !== null && local.minutes + waitTime > hours.close;

    if (closed || full) {
      return {
        shopStatus: closed ? 'Closed' : 'Full',
        waitTime,
        upcomingOpenTime: StoreStatus.formatNextOpen(shop.nextOpenLocal, local.date)
      };
    }

    return { shopStatus: 'Open', waitTime, upcomingOpenTime: '' };
  }

  static shopLocalNow(timeZoneId) {
    let parts = {};
    try {
      parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: timeZoneId || 'UTC',
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        })
          .formatToParts(new Date())
          .map(({ type, value }) => [type, value])
      );
    } catch (error) {
      const now = new Date();
      parts = {
        year: now.getUTCFullYear(), month: String(now.getUTCMonth() + 1).padStart(2, '0'), day: String(now.getUTCDate()).padStart(2, '0'),
        hour: now.getUTCHours(), minute: now.getUTCMinutes()
      };
    }
    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      minutes: Number(parts.hour) * 60 + Number(parts.minute)
    };
  }

  // Today's rolling-week entry wins; the flat todayOpen/CloseLocal fields are the fallback
  static todayHours(shop, localDate) {
    const today = (shop.rollingWeekHours || []).find((h) => h.date === localDate);
    if (today) {
      return { isClosed: today.isClosed === true, open: StoreStatus.parseMinutes(today.openLocal), close: StoreStatus.parseMinutes(today.closeLocal) };
    }
    return { isClosed: false, open: StoreStatus.parseMinutes(shop.todayOpenLocal), close: StoreStatus.parseMinutes(shop.todayCloseLocal) };
  }

  static isClosedNow(shop, local, hours) {
    if (shop.isClosed === true || hours.isClosed) return true;
    if (hours.close === null || hours.open === null) return false;
    return local.minutes >= hours.close || local.minutes < hours.open;
  }

  static parseMinutes(time) {
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
  }

  // nextOpenLocal is shop-local ("2026-09-12T09:00"); today shows time only, other days prefix the weekday
  static formatNextOpen(nextOpenLocal, localDate) {
    const [date, time] = (nextOpenLocal || '').split('T');
    if (!date || !time) return '';

    const opens = StoreStatus.formatTime12h(time);
    if (date === localDate) return `Opens ${opens}`;

    const [year, month, day] = date.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
    return `Opens ${weekday} ${opens}`;
  }

  static formatTime12h(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours < 12 ? 'AM' : 'PM';
    const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${formattedHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  formatWaitTime(waitTime) {
    const minutes = Math.max(0, Number(waitTime) || 0);
    if (minutes === 0) {
      return {
        text: '',
        time: 'Serving Now'
      };
    }

    if (minutes < 60) {
      return { text: 'Estimated', time: `${minutes} Min Wait` };
    }

    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return {
      text: 'Estimated',
      time: remainder === 0 ? `${hours} Hr Wait` : `${hours} Hr ${remainder} Min Wait`
    };
  }

  getWaitTimeContent(data) {
    const { waitTime, shopStatus, upcomingOpenTime = '', message = '' } = data;

    if (shopStatus === 'Open') {
      if (WAIT_TIMES_DISABLED) return { textContent: '', timeContent: WAIT_TIMES_DISABLED_TEXT };

      const formatted = this.formatWaitTime(waitTime);
      return { textContent: formatted.text, timeContent: formatted.time };
    }

    if (shopStatus === 'Closed') {
      return { textContent: upcomingOpenTime, timeContent: 'Shop is Closed' };
    }

    if (shopStatus === 'Full') {
      return { textContent: upcomingOpenTime, timeContent: 'Full for the day' };
    }

    if (shopStatus === 'Disabled') {
      return { textContent: '', timeContent: message };
    }

    // Offline, or the shop could not be loaded
    return { textContent: '', timeContent: 'Please contact Shop' };
  }

  /** Used by store-locator markers; delegates to {@link getWaitTimeContent} for API data. */
  static getDisplayTimeLabel(waitTimeData) {
    const noWaitToShow = WAIT_TIMES_DISABLED && waitTimeData?.shopStatus === 'Open';
    if (!waitTimeData?.shopStatus || noWaitToShow) return 'View Details';
    return StoreStatus.create().getWaitTimeContent(waitTimeData).timeContent;
  }

  /** Sort key matching the Checkins app: Open < Full < Closed < Offline, then minutes. */
  static sortKey(data) {
    const rank = { Open: 0, Full: 1, Closed: 2, Offline: 3 }[data?.shopStatus];
    if (rank === undefined) return Infinity;
    return rank * 100000 + (data.waitTime || 0);
  }

  updateWaitTime(container, storeData) {
    if (!storeData) return;

    const { textContent, timeContent } = this.getWaitTimeContent(storeData);
    const textElement = container.querySelector('.wait-time__text');
    const timeElement = container.querySelector('.wait-time__time');
    const notOpen = ['Closed', 'Full'].includes(storeData.shopStatus);

    const render = () => {
      waitTimeSwaps.delete(container);

      if (textElement) {
        textElement.textContent = textContent;
        textElement.classList.remove('wait-time__text__placeholder');
        textElement.classList.toggle('wait-time__text--reopens', notOpen && !!textContent);
      }

      if (timeElement) {
        timeElement.textContent = timeContent;
        timeElement.classList.remove('wait-time__time__placeholder');
      }

      container.classList.remove('loading-wait-times');
    };

    // A newer update always replaces a swap that is still fading
    clearTimeout(waitTimeSwaps.get(container));

    if (textElement?.textContent === textContent && timeElement?.textContent === timeContent) {
      waitTimeSwaps.delete(container);
      container.classList.remove('loading-wait-times');
    } else {
      // Timer matches the CSS fade; transitionend never fires on hidden containers (closed drawer, collapsed lists)
      container.classList.add('loading-wait-times');
      waitTimeSwaps.set(container, setTimeout(render, 150));
    }

    // Update check-in button visibility
    const checkInButton = container.closest('.location__actions')?.querySelector('.btn--checkin.js-check-in');
    if (checkInButton) {
      checkInButton.style.display = storeData.shopStatus === 'Open' ? 'block' : 'none';
      checkInButton.classList.toggle('shop-closed', storeData.shopStatus === 'Closed');
      checkInButton.classList.toggle('shop-full', storeData.shopStatus === 'Full');
    }

    // Toggle location-open on .check-in-wrapper if store is open
    const checkInWrapper = container.closest('.check-in-wrapper');
    if (checkInWrapper) {
      checkInWrapper.classList.toggle('location-open', storeData.shopStatus === 'Open');
    }
  }

  static create() {
    return new StoreStatus();
  }
}

document.addEventListener('visibilitychange', () => NexusWaits.syncHub());

// Ends live updates for a wrapper that is no longer on screen (a closed drawer)
document.addEventListener('dispatch:shop-data:stop', (e) => {
  e.detail?.wrapper?.querySelectorAll('.js-wait-time').forEach((element) => NexusWaits.unwatch(element));
});
setInterval(() => NexusWaits.tick(), STATUS_TICK_MS);

// Wait-time request for a wrapper; filter is the nexus shopId (location_basic_info.shopId)
document.addEventListener('dispatch:shop-data', async function (e) {
  const { waitTimes = false, wrapper = null, filter = null } = e.detail || {};
  if (!waitTimes || !wrapper) return;

  const shopId =
    filter ||
    wrapper.closest('[data-nexus-shop-id]')?.dataset.nexusShopId ||
    wrapper.querySelector('[data-nexus-shop-id]')?.dataset.nexusShopId;
  if (!shopId) return;

  const instance = StoreStatus.create();
  const storeData = await instance.fetchShopStatus(shopId);

  wrapper.querySelectorAll('.has-wait-time').forEach(container => {
    const waitTimeElement = container.querySelector('.js-wait-time');
    if (!waitTimeElement) return;
    NexusWaits.watch(shopId, waitTimeElement);
    instance.updateWaitTime(waitTimeElement, storeData);
  });
});
